import type { AnalyticsClient } from '../../analytics/client';
import type { DiagnosticUploader } from '../../analytics/diagnostic-uploader';
import type { AnalyticsIdentity } from '../../analytics/contract';
import type { SessionTracker } from '../../analytics/session-tracker';
import type { GameAudio } from '../../audio/audio';
import { saveSettings, type AppSettings } from '../settings';
import type { I18nPort } from '../../i18n/i18n-service';
import { SUPPORTED_LOCALES, type AppLocale } from '../../i18n/types';
import { clearDiagnosticHistory, clearDiagnosticUploadQueue } from '../../storage/diagnostics-store';
import { requireElement } from '../dom';
import type { ToastController } from './toast-controller';

export interface SettingsControllerOptions {
  readonly settings: AppSettings;
  readonly i18n: I18nPort;
  readonly audio: GameAudio;
  readonly analytics: AnalyticsClient;
  readonly analyticsIdentity: AnalyticsIdentity;
  readonly sessionTracker: SessionTracker;
  readonly diagnosticUploader: DiagnosticUploader;
  readonly toast: ToastController;
  readonly setRendererQuality: (quality: AppSettings['quality']) => void;
  readonly onLocalLogsCleared: () => void;
  readonly onClosed: () => void;
}

export class SettingsController {
  private readonly screen = requireElement<HTMLElement>('#settingsScreen');
  private readonly musicVolume = requireElement<HTMLInputElement>('#musicVolume');
  private readonly sfxVolume = requireElement<HTMLInputElement>('#sfxVolume');
  private readonly mute = requireElement<HTMLInputElement>('#muteSetting');
  private readonly motion = requireElement<HTMLInputElement>('#motionSetting');
  private readonly quality = requireElement<HTMLSelectElement>('#qualitySetting');
  private readonly locale = requireElement<HTMLSelectElement>('#localeSetting');
  private readonly metrics = requireElement<HTMLInputElement>('#metricsSetting');
  private readonly logUpload = requireElement<HTMLInputElement>('#logUploadSetting');
  private readonly deleteServerLogs = requireElement<HTMLButtonElement>('#deleteServerLogsButton');
  private readonly clearLogs = requireElement<HTMLButtonElement>('#clearLogsButton');
  private deleteServerArmedUntil = 0;
  private clearLogsArmedUntil = 0;

  constructor(private readonly options: SettingsControllerOptions) {
    this.renderLocaleOptions();
    this.syncControls();
    this.bindEvents();
    this.options.i18n.subscribe(() => this.renderLocaleOptions());
  }

  open(): void { this.screen.classList.add('open'); }

  close(): void {
    this.screen.classList.remove('open');
    this.persist();
    this.options.onClosed();
  }

  persist(): void {
    const { settings, i18n, audio } = this.options;
    if (!saveSettings(localStorage, settings)) this.options.toast.show(i18n.t('settings.savedFailed'));
    document.body.classList.toggle('reduce-motion', settings.reducedMotion);
    audio.sync();
    this.options.setRendererQuality(settings.quality);
  }

  private bindEvents(): void {
    requireElement<HTMLButtonElement>('#closeSettings').addEventListener('click', () => this.close());
    this.musicVolume.addEventListener('input', () => { this.options.settings.musicVolume = Number(this.musicVolume.value) / 100; this.persist(); });
    this.sfxVolume.addEventListener('input', () => { this.options.settings.sfxVolume = Number(this.sfxVolume.value) / 100; this.persist(); });
    this.mute.addEventListener('change', () => { this.options.settings.muted = this.mute.checked; this.persist(); });
    this.motion.addEventListener('change', () => { this.options.settings.reducedMotion = this.motion.checked; this.persist(); });
    this.quality.addEventListener('change', () => { this.options.settings.quality = this.quality.value as AppSettings['quality']; this.persist(); });
    this.locale.addEventListener('change', () => {
      const locale = this.locale.value as AppLocale;
      this.options.settings.locale = locale;
      this.options.i18n.setLocale(locale);
      this.options.i18n.applyDocument();
      this.persist();
    });
    this.metrics.addEventListener('change', () => {
      this.options.settings.shareAnonymousMetrics = this.metrics.checked;
      this.persist();
      if (this.options.settings.shareAnonymousMetrics) {
        this.options.analytics.event('app_loaded', { source: 'consent-enabled' });
        this.options.analytics.session(this.options.sessionTracker.snapshot());
      }
    });
    this.logUpload.addEventListener('change', () => { void this.toggleLogUploads(); });
    this.deleteServerLogs.addEventListener('click', () => { void this.deleteUploadedLogs(); });
    this.clearLogs.addEventListener('click', () => { void this.clearLocalLogs(); });
  }

  private syncControls(): void {
    const settings = this.options.settings;
    this.musicVolume.value = String(settings.musicVolume * 100);
    this.sfxVolume.value = String(settings.sfxVolume * 100);
    this.mute.checked = settings.muted;
    this.motion.checked = settings.reducedMotion;
    this.quality.value = settings.quality;
    this.locale.value = this.options.i18n.locale;
    this.metrics.checked = settings.shareAnonymousMetrics;
    this.logUpload.checked = settings.uploadDiagnosticLogs;
  }

  private renderLocaleOptions(): void {
    const labels: Record<AppLocale, string> = { en: 'English', 'zh-Hans': '简体中文', ru: 'Русский', es: 'Español' };
    this.locale.replaceChildren();
    for (const locale of SUPPORTED_LOCALES) {
      const option = document.createElement('option'); option.value = locale; option.textContent = labels[locale]; option.selected = locale === this.options.i18n.locale; this.locale.append(option);
    }
  }

  private async toggleLogUploads(): Promise<void> {
    const { settings, i18n, toast } = this.options;
    settings.uploadDiagnosticLogs = this.logUpload.checked;
    this.persist();
    if (settings.uploadDiagnosticLogs) {
      void this.options.diagnosticUploader.process();
      toast.show(i18n.t('settings.logsEnabled'));
    } else {
      try { await clearDiagnosticUploadQueue(); } catch { /* The preference remains authoritative. */ }
      toast.show(i18n.t('settings.logsDisabled'));
    }
  }

  private async deleteUploadedLogs(): Promise<void> {
    const { i18n, toast } = this.options;
    if (Date.now() > this.deleteServerArmedUntil) {
      this.deleteServerArmedUntil = Date.now() + 4_000;
      this.deleteServerLogs.textContent = i18n.t('settings.deleteServerConfirm');
      window.setTimeout(() => { if (Date.now() >= this.deleteServerArmedUntil && !this.deleteServerLogs.disabled) this.deleteServerLogs.textContent = i18n.t('settings.deleteServerLogs'); }, 4_050);
      return;
    }
    this.deleteServerArmedUntil = 0;
    this.deleteServerLogs.disabled = true;
    try {
      const response = await fetch('/api/diagnostics-delete', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin', body: JSON.stringify(this.options.analyticsIdentity) });
      if (!response.ok && response.status !== 403) throw new Error(`HTTP ${response.status}`);
      this.deleteServerLogs.textContent = i18n.t(response.ok ? 'settings.serverLogsDeleted' : 'settings.serverLogsMissing');
      toast.show(i18n.t(response.ok ? 'settings.serverLogsDeletedToast' : 'settings.serverLogsMissingToast'));
    } catch (error) {
      this.deleteServerLogs.textContent = i18n.t('settings.serverDeleteFailed');
      toast.show(i18n.t('settings.serverDeleteError', { error: String(error) }));
    } finally {
      window.setTimeout(() => { this.deleteServerLogs.disabled = false; this.deleteServerLogs.textContent = i18n.t('settings.deleteServerLogs'); }, 1_800);
    }
  }

  private async clearLocalLogs(): Promise<void> {
    const { i18n, toast } = this.options;
    if (Date.now() > this.clearLogsArmedUntil) {
      this.clearLogsArmedUntil = Date.now() + 4_000;
      this.clearLogs.textContent = i18n.t('settings.clearConfirm');
      window.setTimeout(() => { if (Date.now() >= this.clearLogsArmedUntil) this.clearLogs.textContent = i18n.t('settings.clearLogs'); }, 4_050);
      return;
    }
    this.clearLogsArmedUntil = 0;
    this.clearLogs.disabled = true;
    try {
      await clearDiagnosticHistory();
      this.options.onLocalLogsCleared();
      this.clearLogs.textContent = i18n.t('settings.logsCleared');
      toast.show(i18n.t('settings.logsClearDone'));
    } catch (error) {
      this.clearLogs.textContent = i18n.t('settings.logsClearFailed');
      toast.show(i18n.t('settings.logsClearError', { error: String(error) }));
    } finally {
      window.setTimeout(() => { this.clearLogs.disabled = false; this.clearLogs.textContent = i18n.t('settings.clearLogs'); }, 1_800);
    }
  }
}
